import type { ILocalizedSite } from '@/constructs/LocaleSchema';
import I18NService from '@/services/isomorphic/I18NService';
import LoggerService from '@/services/isomorphic/LoggerService';
import { normalizeFilterString } from './utils';

/**
 * Describes a matcher: a function that rates how much a provided filter string
 * matches a configured localized site.
 *
 * Aliases and URL pathnames should be factored in when calculating these
 * values.
 */
export type LocalizedSiteMatcher = (
  site: ILocalizedSite,
  filterString: string
) => number;

/**
 * Given two strings, normalize both and find out if the second string is a
 * substring of the first one.
 *
 * @param searchString - String to search on.
 * @param substring - Substring to find.
 *
 * @returns A number signifying the index where the substring was found on the
 * search string (if found at all). If the search string does not contain the
 * substring, `-1` will be returned instead.
 */
const normalizeAndSearch = (
  searchString: string,
  substring: string
): number => {
  const normalizedSearchString = normalizeFilterString(searchString);
  const normalizedSubstring = normalizeFilterString(substring);

  return normalizedSearchString.indexOf(normalizedSubstring);
};

/**
 * {@link LocalizedSiteMatcher Matcher} that determines if the alias for the
 * site corresponding to the current language contains the filter string.
 *
 * @param site - Site to search.
 * @param filterString - Filter string to search.
 *
 * @returns A number signifying the index where the substring was found on the
 * site's data (if found at all). If the site's data does not contain the
 * substring, `-1` will be returned instead.
 */
export const matchByCurrentAlias: LocalizedSiteMatcher = (
  site,
  filterString
) => {
  const currentAlias = site.aliases[I18NService.currentLocale.language];

  if (!currentAlias) {
    LoggerService.warn(
      `Cannot list site "${site.id}" as it does not have a valid alias ` +
        `for the current language.`
    );

    return -1;
  }

  return normalizeAndSearch(currentAlias, filterString);
};

/**
 * {@link LocalizedSiteMatcher Matcher} that determines if any of the aliases of
 * the current site (i.e. in any language) contains the filter string.
 *
 * @param site - Site to search.
 * @param filterString - Filter string to search.
 *
 * @returns A number signifying the index where the substring was found on the
 * site's data (if found at all). If the site's data does not contain the
 * substring, `-1` will be returned instead.
 */
export const matchByAllAliases: LocalizedSiteMatcher = (site, filterString) => {
  const matchValues = Object.values(site?.aliases ?? []).map((alias) => {
    const matchValue = normalizeAndSearch(alias, filterString);
    return matchValue >= 0 ? matchValue : Infinity;
  });

  const minValue = Math.min(...matchValues);

  return minValue === Infinity ? -1 : minValue;
};

/**
 * {@link LocalizedSiteMatcher Matcher} that determines if the URL pathname for
 * the current site contains the filter string.
 *
 * @param site - Site to search.
 * @param filterString - Filter string to search.
 *
 * @returns A number signifying the index where the substring was found on the
 * site's data (if found at all). If the site's data does not contain the
 * substring, `-1` will be returned instead.
 */
export const matchByURLPath: LocalizedSiteMatcher = (site, filterString) => {
  const { url } = site;

  const normalizedPathname = normalizeFilterString(url.pathname);
  return normalizeAndSearch(normalizedPathname, filterString);
};
